/**
 * Biometric authentication hook.
 *
 * Manages biometric enrollment (create WebAuthn credential + store PIN),
 * disabling (remove biometric data), and unlock (assert biometric + decrypt).
 *
 * The biometric flow: assert WebAuthn credential -> retrieve encrypted PIN ->
 * decrypt mnemonic via normal PIN path -> unlock MnemonicProvider.
 *
 * Tier: All tiers (core auth infrastructure).
 *
 * @module useBiometric
 */

import { useCallback, useEffect, useState } from 'react';
import { decryptMnemonic, PinError } from '../../../lib/crypto-encrypt';
import { useMnemonic } from '../../../contexts/MnemonicProvider';
import { createPlatformServices } from '../../../lib/platform/index';
import {
  isWebAuthnAvailable,
  createBiometricCredential,
  assertBiometric,
  storePinForBiometric,
  retrievePinViaBiometric,
  removeBiometricData,
  storeCredentialId,
  getStoredCredentialId,
} from '../../../lib/webauthn';

/** SecureStorage key for the encrypted master mnemonic. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/** Return type for the useBiometric hook. */
export interface UseBiometricReturn {
  /** Whether WebAuthn biometric auth is available on this device. */
  readonly isAvailable: boolean;
  /** Whether biometric auth is currently enrolled (credential exists). */
  readonly isEnabled: boolean;
  /** Whether a biometric operation is in progress. */
  readonly isLoading: boolean;
  /** Error message from the last failed operation, or null. */
  readonly error: string | null;
  /**
   * Enable biometric auth by creating a credential and storing the PIN.
   *
   * @param pin - The current PIN to store encrypted for biometric unlock
   */
  enable(pin: string): Promise<void>;
  /** Disable biometric auth by removing all biometric data. */
  disable(): Promise<void>;
  /**
   * Unlock the wallet using biometric authentication.
   *
   * Asserts the WebAuthn credential, retrieves the stored PIN,
   * and decrypts the mnemonic.
   *
   * @returns true if unlock succeeded, false otherwise
   */
  unlockWithBiometric(): Promise<boolean>;
  /** Clear any displayed error. */
  clearError(): void;
}

/**
 * Hook for biometric authentication operations.
 *
 * Checks availability on mount and tracks enrollment state via
 * stored credential ID in IndexedDB.
 *
 * Tier: All tiers (core auth infrastructure).
 *
 * @returns UseBiometricReturn with enable/disable/unlock and state
 */
export function useBiometric(): UseBiometricReturn {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { unlock } = useMnemonic();

  // Check availability and enrollment on mount
  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      const available = await isWebAuthnAvailable();
      if (cancelled) return;
      setIsAvailable(available);

      const credentialId = await getStoredCredentialId();
      if (cancelled) return;
      setIsEnabled(credentialId !== null);
    }

    void check();

    return (): void => {
      cancelled = true;
    };
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const enable = useCallback(async (pin: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Create WebAuthn credential (prompts biometric)
      const credential = await createBiometricCredential();

      // Store credential ID for future assertions
      await storeCredentialId(credential.rawId);

      // Store PIN encrypted for biometric unlock
      await storePinForBiometric(pin);

      setIsEnabled(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to enable biometric';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const disable = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await removeBiometricData();
      setIsEnabled(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disable biometric';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unlockWithBiometric = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Get stored credential ID
      const credentialId = await getStoredCredentialId();
      if (credentialId === null) {
        setError('Biometric not enrolled');
        return false;
      }

      // Assert biometric (prompts user)
      const verified = await assertBiometric(credentialId);
      if (!verified) {
        setError('Biometric verification failed');
        return false;
      }

      // Retrieve PIN from encrypted store
      const pin = await retrievePinViaBiometric();

      // Decrypt mnemonic via normal PIN path
      const services = await createPlatformServices();
      const encrypted = await services.secureStorage.retrieve(ENCRYPTED_MNEMONIC_KEY);

      if (encrypted === null) {
        setError('No wallet found');
        return false;
      }

      const mnemonic = await decryptMnemonic(encrypted, pin);
      unlock(mnemonic);
      return true;
    } catch (err) {
      if (err instanceof PinError) {
        // Stored PIN no longer matches the current mnemonic encryption.
        // This happens when the wallet was recreated or PIN was changed
        // without re-enrolling biometric. Auto-disable stale enrollment.
        try {
          await removeBiometricData();
        } catch {
          // Best-effort cleanup
        }
        setIsEnabled(false);
        setError('Biometric needs to be set up again. Log in with your PIN.');
        return false;
      }
      const message = err instanceof Error ? err.message : 'Biometric unlock failed';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [unlock]);

  return {
    isAvailable,
    isEnabled,
    isLoading,
    error,
    enable,
    disable,
    unlockWithBiometric,
    clearError,
  };
}
